/**
 * Folding ant's live events into the console's state. Pure, so it is
 * unit-tested on its own; useChat wires it to the socket.
 *
 * Two things ant guarantees nothing about shape this code: events may arrive
 * in any order, and a resend with the same clientMessageId publishes the same
 * message (same id) again. So everything keys by id and threads sort by
 * insertedAt.
 */

import { AntChatMessage, AntChatSession } from './api';
import { isAfter, laterOf, mapSession, messagePreview, sameList } from './mappers';
import { Conversation, Message } from './types';

/**
 * Session metadata and message events travel independently. A tag/assignment
 * response can be newer metadata while its message preview predates a socket
 * event. Preserve that newer activity, and context only a thread supplies.
 */
export function keepDerivedContext(
  next: Conversation,
  previous: Conversation | undefined
): Conversation {
  if (!previous) return next;
  const newerMessage =
    previous.last_message &&
    (!next.last_message ||
      isAfter(previous.last_message.created_on, next.last_message.created_on));
  return {
    ...next,
    updated_on: laterOf(next.updated_on, previous.updated_on) ?? next.updated_on,
    last_message: newerMessage ? previous.last_message : next.last_message,
    waiting_since: newerMessage ? previous.waiting_since : next.waiting_since,
    last_read_at_admin: laterOf(next.last_read_at_admin, previous.last_read_at_admin),
    last_read_at_user: laterOf(next.last_read_at_user, previous.last_read_at_user),
    context_rental_id: next.context_rental_id ?? previous.context_rental_id,
    context_hardware_id: next.context_hardware_id ?? previous.context_hardware_id,
    user_lat: next.user_lat ?? previous.user_lat,
    user_lng: next.user_lng ?? previous.user_lng,
  };
}

const sameRow = (a: Conversation, b: Conversation): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * A session as ant just published it, folded into the queue: a new one is
 * appended, a known one replaced (keeping what its thread taught us), a
 * closed one leaves, and so does one the assistant is answering (it has
 * its own list, foldAssistantSession). An escalated or taken-over session
 * comes in. Returns the same array when nothing changed.
 */
export function foldSession(
  conversations: Conversation[],
  session: AntChatSession
): Conversation[] {
  const mapped = mapSession(session);
  const index = conversations.findIndex((c) => c.id === mapped.id);

  if (mapped.status === 'closed' || mapped.handled_by === 'assistant') {
    return index === -1 ? conversations : conversations.filter((c) => c.id !== mapped.id);
  }
  if (index === -1) return [...conversations, mapped];

  const next = keepDerivedContext(mapped, conversations[index]);
  if (sameRow(next, conversations[index])) return conversations;
  const out = [...conversations];
  out[index] = next;
  return out;
}

const byCreatedOn = (a: Message, b: Message): number =>
  Date.parse(a.created_on) - Date.parse(b.created_on);

/**
 * A message published for a loaded thread: appended in insertedAt order, or
 * ignored when the thread already holds its id (a resend). Returns the same
 * array when nothing changed.
 */
export function foldMessage(thread: Message[], message: Message): Message[] {
  if (thread.some((m) => m.id === message.id)) return thread;
  return [...thread, message].sort(byCreatedOn);
}

/**
 * A freshly fetched thread merged with the one on screen. Messages are never
 * deleted, so the union by id is the truth: the fetch wins for a message both
 * hold (an attachment may have been approved since), and a message the socket
 * delivered while the fetch was in flight is not lost.
 */
export function mergeThread(existing: Message[], fetched: Message[]): Message[] {
  const fetchedIds = new Set(fetched.map((m) => m.id));
  const onlyExisting = existing.filter((m) => !fetchedIds.has(m.id));
  if (onlyExisting.length === 0) return fetched;
  return [...fetched, ...onlyExisting].sort(byCreatedOn);
}

/**
 * What a published message says about its session in the queue: when the
 * rider last wrote and from where, the hardware and rental they wrote about.
 * A message older than the newest known one does not move `last_message`,
 * and neither does an internal note: the rider did not get an answer, so
 * the session is still waiting (and still unread) as it was.
 */
export function foldMessageIntoConversation(
  conversation: Conversation,
  row: AntChatMessage,
  message: Message
): Conversation {
  const isNote = message.sender_type === 'note';
  const newest =
    !isNote &&
    (!conversation.last_message ||
      isAfter(message.created_on, conversation.last_message.created_on));
  const isRider = message.sender_type === 'user';
  // The SLA clock, as ant keeps it: a reply stops it, and a rider message
  // starts it only when it is not already running.
  const waitingSince = !newest
    ? conversation.waiting_since
    : isRider
    ? conversation.last_message?.sender_type === 'user'
      ? conversation.waiting_since ?? null
      : message.created_on
    : null;
  const located =
    isRider && typeof row.sentLat === 'number' && typeof row.sentLng === 'number';

  const next: Conversation = {
    ...conversation,
    updated_on: isNote
      ? conversation.updated_on
      : laterOf(conversation.updated_on, message.created_on) ?? conversation.updated_on,
    last_message: newest
      ? {
          sender_type: message.sender_type,
          created_on: message.created_on,
          preview: messagePreview(
            message.content,
            message.content_type === 'video'
              ? 'video'
              : message.content_type === 'image'
              ? 'photo'
              : null
          ),
          sender_name: isRider ? null : message.sender_name || null,
        }
      : conversation.last_message,
    ...(waitingSince === undefined ? {} : { waiting_since: waitingSince }),
    user_lat: located ? row.sentLat : conversation.user_lat,
    user_lng: located ? row.sentLng : conversation.user_lng,
    context_hardware_id: row.contextHardwareId ?? conversation.context_hardware_id,
    // The session's own rental wins; a general session shows the rental the
    // rider last wrote about.
    context_rental_id: conversation.context_rental_id ?? row.rentalId ?? null,
  };
  return sameRow(next, conversation) ? conversation : next;
}

/** `foldMessageIntoConversation` over the queue, keeping the reference when nothing changed. */
export function foldMessageIntoQueue(
  conversations: Conversation[],
  row: AntChatMessage,
  message: Message
): Conversation[] {
  const next = conversations.map((c) =>
    c.id === message.conversation_id ? foldMessageIntoConversation(c, row, message) : c
  );
  return sameList(conversations, next) ? conversations : next;
}

const closedFirst = (a: Conversation, b: Conversation): number =>
  (Date.parse(b.closed_at ?? b.updated_on) || 0) -
  (Date.parse(a.closed_at ?? a.updated_on) || 0);

/**
 * The closed sessions the console has seen (browsed, or closed while it
 * watched), newest close first. A closed session is inserted or replaced;
 * one that is not closed does not belong here. Returns the same array when
 * nothing changed.
 */
export function foldClosedSession(
  closed: Conversation[],
  session: AntChatSession
): Conversation[] {
  const mapped = mapSession(session);
  const index = closed.findIndex((c) => c.id === mapped.id);
  if (mapped.status !== 'closed') {
    return index === -1 ? closed : closed.filter((c) => c.id !== mapped.id);
  }
  if (index !== -1) {
    const next = keepDerivedContext(mapped, closed[index]);
    if (sameRow(next, closed[index])) return closed;
    const out = [...closed];
    out[index] = next;
    return out.sort(closedFirst);
  }
  return [...closed, mapped].sort(closedFirst);
}

/**
 * The assistant's sessions: one the assistant answers is inserted or
 * replaced; one a person took (escalated, taken over) or that closed
 * leaves. Returns the same array when nothing changed.
 */
export function foldAssistantSession(
  assistant: Conversation[],
  session: AntChatSession
): Conversation[] {
  const mapped = mapSession(session);
  const index = assistant.findIndex((c) => c.id === mapped.id);
  if (mapped.status === 'closed' || mapped.handled_by !== 'assistant') {
    return index === -1 ? assistant : assistant.filter((c) => c.id !== mapped.id);
  }
  if (index === -1) return [...assistant, mapped];
  const next = keepDerivedContext(mapped, assistant[index]);
  if (sameRow(next, assistant[index])) return assistant;
  const out = [...assistant];
  out[index] = next;
  return out;
}
